import { Component, type ReactNode, Suspense, useEffect, useState } from "react";

import { ApiError, apiClient } from "../../api/client";
import type { MeResponse, VisionView } from "../../api/types";
import { hasPermission } from "../../auth/permissions";
import { useIdempotencyKey } from "../../lib/idempotencyKey";
import { SeasonPicker } from "../blocks/SeasonPicker";
import { useBlockSeason } from "../blocks/useBlockSeason";
import { VISION_COLOR_PALETTE } from "./visionColors";
import { DEFAULT_VISUALIZER_ID, getVisualizer } from "./visualizers";

interface State {
  status: "loading" | "ready" | "error";
  vision: VisionView | null;
  errorMessage: string | null;
}

const initial: State = { status: "loading", vision: null, errorMessage: null };

// A render can mount a WebGL canvas; a thrown error there must not unwind the
// whole page (and lose the GL context). This boundary contains the failure to
// the visualizer slot.
class VisualizerBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  render() {
    if (this.state.failed) {
      return <div className="vision-empty">The render hit an error. Reload to retry.</div>;
    }
    return this.props.children;
  }
}

export function VisionPage({ me }: { me: MeResponse }) {
  const [state, setState] = useState<State>(initial);
  // Admin-only "fully realized" preview. The button is hidden for non-admins
  // and the /vision/full endpoint is permission-gated, so preview can only be
  // true for someone the server will serve.
  const [preview, setPreview] = useState(false);
  const canPreview = hasPermission(me, "blocks:vision:preview");
  // Leadership can preview a non-active season's sculpture.
  const { seasonId, seasons, setSeasonId, ready } = useBlockSeason(me);
  // Bumped after the viewer changes their Vision color, to refetch and recolor.
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!ready) {
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        const vision = await (preview
          ? apiClient.getVisionFull(seasonId)
          : apiClient.getVision(seasonId));
        if (cancelled) {
          return;
        }
        setState({ status: "ready", vision, errorMessage: null });
      } catch (caught) {
        if (cancelled) {
          return;
        }
        setState({ status: "error", vision: null, errorMessage: messageFor(caught) });
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [preview, seasonId, refreshTick, ready]);

  if (state.status === "loading") {
    return (
      <div className="page--vision">
        <div className="loading">Loading the Vision…</div>
      </div>
    );
  }

  if (state.status === "error" || state.vision === null) {
    return (
      <div className="page--vision">
        <div className="card empty-state">
          <h2>Couldn't load the Vision</h2>
          <p>{state.errorMessage ?? "Something went wrong."}</p>
        </div>
      </div>
    );
  }

  const vision = state.vision;

  // No blocks in the active season (or no active season) — nothing to sculpt.
  // Skip mounting the 3D canvas entirely.
  if (vision.total_val === 0) {
    return (
      <div className="page--vision">
        <div className="card empty-state">
          <h2>The Phoenix awaits</h2>
          <p>
            No blocks in this season yet. As block value is realized, the Phoenix
            assembles itself.
          </p>
          <div className="vision-empty-season-picker form-row">
            <SeasonPicker seasons={seasons} value={seasonId} onChange={setSeasonId} />
          </div>
        </div>
      </div>
    );
  }

  const fraction = clamp01(vision.realized_val / vision.total_val);
  const pct = Math.round(fraction * 100);
  const Visualizer = getVisualizer(DEFAULT_VISUALIZER_ID).Component;

  return (
    <div className="page--vision">
      <VisualizerBoundary key={DEFAULT_VISUALIZER_ID}>
        <Suspense fallback={<div className="vision-empty">Summoning the Phoenix…</div>}>
          <Visualizer vision={vision} me={me} onVisionRefresh={() => setRefreshTick((tick) => tick + 1)} />
        </Suspense>
      </VisualizerBoundary>
      <div className="vision-season-picker form-row">
        <SeasonPicker seasons={seasons} value={seasonId} onChange={setSeasonId} />
      </div>
      <MyColorPicker current={vision.my_color} onPicked={() => setRefreshTick((tick) => tick + 1)} />
      <div className="vision-stats">
        <div className="vision-stats__realized">{pct}% realized</div>
        <div className="vision-stats__detail">
          {Math.round(vision.realized_val).toLocaleString()} / {vision.total_val.toLocaleString()}{" "}
          blocks
        </div>
        {preview ? <div className="vision-stats__preview">Preview · fully realized</div> : null}
      </div>
      {canPreview ? (
        <button
          type="button"
          className="btn btn--ghost btn--sm vision-preview-toggle"
          onClick={() => setPreview((shown) => !shown)}
        >
          {preview ? "Show actual progress" : "Preview fully realized"}
        </button>
      ) : null}
    </div>
  );
}

// MyColorPicker is the compact overlay control for the viewer's own Vision color:
// a trigger showing the current swatch that opens a grid of the palette plus a
// "Default" option that clears it. On a pick it writes the color and asks the
// page to refetch so the sculpture recolors this person's territory.
function MyColorPicker({ current, onPicked }: { current: string | null; onPicked: () => void }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { keyFor, reset } = useIdempotencyKey();

  async function choose(hex: string) {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.setVisionColor(hex, keyFor(hex === "" ? "clear" : hex));
      reset();
      setOpen(false);
      onPicked();
    } catch (caught) {
      // Best-effort: leave the picker open so the user can retry.
      // The server replays a stored answer for a reused key, so a refused
      // request needs a new key to be retried; a network error may never have
      // reached the server, so that retry keeps the key.
      if (caught instanceof ApiError) {
        reset();
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="vision-color">
      {open && (
        <div className="vision-color__menu" role="menu">
          <button
            type="button"
            className={`vision-color__swatch vision-color__swatch--default${current === null ? " is-selected" : ""}`}
            onClick={() => void choose("")}
            disabled={submitting}
            title="Default (no color)"
          >
            Default
          </button>
          {VISION_COLOR_PALETTE.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`vision-color__swatch${current === option.hex ? " is-selected" : ""}`}
              style={{ background: option.hex }}
              onClick={() => void choose(option.hex)}
              disabled={submitting}
              aria-label={option.label}
              title={option.label}
            />
          ))}
        </div>
      )}
      <button
        type="button"
        className="vision-color__trigger"
        onClick={() => setOpen((shown) => !shown)}
        aria-expanded={open}
      >
        <span
          className="vision-color__chip"
          style={current !== null ? { background: current } : undefined}
        />
        My color
      </button>
    </div>
  );
}

function clamp01(n: number): number {
  if (n < 0) {
    return 0;
  }
  if (n > 1) {
    return 1;
  }
  return n;
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return "Something went wrong.";
}
